/**********************************************************
Hawaiian Macadamia Nut Services
Alexander Self
7/14/20
Irrigation Actions
**********************************************************/
import axios from 'axios';
import moment from 'moment';
import { FETCH_IRRIGATION, FETCH_IRRIGATION_EMPLOYEES, PREPARE_IRRIGATION_DATA,
  HANDLE_VALVE_HOURS, HANDLE_ENGINE_HOURS, HANDLE_METER_GALLONS, CREATE_IRRIGATION,
  PRELOAD_METER_FORM, SELECT_IRRIGATION_LOG_TO_EDIT, DELETE_IRRIGATION,
  HANDLE_EDIT_VALVE_HOURS, HANDLE_EDIT_METER_GALLONS, HANDLE_EDIT_ENGINE_HOURS, PREPARE_EDIT_IRRIGATION_DATA,
  EDIT_IRRIGATION, FETCH_IRRIGATION_REPORT, RESET_DASHBOARD, ERROR } from './types';
import { ROOT_URL } from '../constants';
import { generateID, sumProp, preFillForm, preFillMeterForm, clearForm } from './irrigation_utils';
import { findEmployeeByName } from '../utils';
const Materialize = window.Materialize;
const $ = window.$;
/**
* fetchIrrigation(): Fetches irrigation records
* @returns {Object} {Array} engines, meters, valves:
* @called-in: ['/irrigation/irrigation_view.js']
**/
export function fetchIrrigation() {
  return (dispatch) => {
    axios.get(`${ROOT_URL}/irrigation`, {
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
      dispatch({ type: FETCH_IRRIGATION, payload: res.data });
    }).catch((err) => { console.error(err) })
  }
}

/**
* fetchIrrigationReport(): Fetches an irrigation report bassd on startDate, endDate
* @returns {Object} {Array} engines, meters, valves:
* @called-in: ['/irrigation/irrigation_view.js']
**/
export function fetchIrrigationReport({ startDate, endDate, email }) {
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.get(`${ROOT_URL}/irrigationReport`, {
      params: { startDate, endDate, email },
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
      $('#preloader').css('display','none');
      //console.log(res.data)
      success(`Success fetching irrigation report for ${startDate} to ${endDate}`);
      dispatch({ type: FETCH_IRRIGATION_REPORT, payload: res.data, startDate, endDate });
    }).catch((err) => {
      $('#preloader').css('display','none');
      console.error(err)
    });
  }
}

/**
* fetchEmployees(): Fetches all active BU employees
* @returns {Array} employees
* @called-in: ['/components/chem_app/chem_app_create.js']
**/
export function fetchEmployees() {
  return (dispatch) => {
    axios.get(`${ROOT_URL}/fetchEmployees`, {
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
        dispatch({ type: FETCH_IRRIGATION_EMPLOYEES, payload: res.data.employees });
      }).catch((err) => console.error(err));
  }
}

/**
* createIrrigation(): Creates a new irrigation record
* @param {Object} app: data, engineLog, meterLog, valveLog
* @param {Array} employees: list of all BU employees
* @param {String} formId
* @returns {Object} app: data, engineLog, meterLog, valveLog
* @called-in: ['/irrigation/irrigation_create.js']
**/
export function createIrrigation({ app, employees, formId }) {
  // Validations
  // Case 1 Date, Employee required
  let { date, employee } = app.data;
  if (!(date && employee)) return error('Please select a Date and Employee.');
  // Case 2 If valve or meter, engine hours must be added and vise versa
  let { engineLog, meterLog, valveLog } = app;
  if (valveLog.length === 0 && engineLog.length === 0 && meterLog.length === 0) return error('Please add at least one meter, valve and engine.');
  if (valveLog.length > 0 && (engineLog.length === 0 || meterLog.length === 0)) return error('Please add engines and meters with valves.');
  if (meterLog.length > 0 && (engineLog.length === 0 || valveLog.length === 0)) return error('Please add engines and valves with meters.');
  if (engineLog.length > 0 && (meterLog.length === 0 || valveLog.length === 0)) return error('Please add meters and valves with engines.');

  let emp = findEmployeeByName(employee, employees);
  app.data.employee = emp.id;

  return (dispatch) => {
    axios.post(`${ROOT_URL}/createIrrigation`, { app })
      .then((res) => {
        success('Success creating a new irrigation record');
        clearForm(formId);
        clearForm('engine-form');
        clearForm('meter-form');
        clearForm('valve-form');
        setTimeout(() => {
          preFillForm({ data: res.data.prevMeters, formId: 'meter-form', prop: 'endGal' })
        }, 200);
        dispatch({ type: CREATE_IRRIGATION, payload: res.data });
    }).catch((err) => { error(err.response.statusText) });
  }
}

/**
* editIrrigation(): Edits an existing irrigation record
* @param {Object} app: data, engineLog, meterLog, valveLog
* @param {Array} employees: list of all BU employees
* @param {String} formId
* @returns {Object} app: data, engineLog, meterLog, valveLog
* @called-in: ['/irrigation/irrigation_create.js']
**/
export function editIrrigation({ app, deleted, employees, formId }) {
  // Validations
  // Case 1 Date, Employee required
  let { date, employee } = app.data;
  if (!(date && employee)) return error('Please select a Date and Employee.');
  // Case 2 If valve or meter, engine hours must be added and vise versa
  let { engineLog, meterLog, valveLog } = app;
  if (valveLog.length === 0 && engineLog.length === 0 && meterLog.length === 0) return error('Please add at least one meter, valve and engine.');
  if (valveLog.length > 0 && (engineLog.length === 0 || meterLog.length === 0)) return error('Please add engines and meters with valves.');
  if (meterLog.length > 0 && (engineLog.length === 0 || valveLog.length === 0)) return error('Please add engines and valves with meters.');
  if (engineLog.length > 0 && (meterLog.length === 0 || valveLog.length === 0)) return error('Please add meters and valves with engines.');

  let emp = findEmployeeByName(employee, employees);
  app.data.employee = emp.id;

  let _deleted = {
    engineLog: deleted.engineLog.map((e) => e.id),
    meterLog: deleted.meterLog.map((e) => e.id),
    valveLog: deleted.valveLog.map((e) => e.id)
  };

  return (dispatch) => {
    axios.post(`${ROOT_URL}/editIrrigation`, { app, deleted: _deleted })
      .then((res) => {
        success('Success editing an irrigation record');
        clearForm(formId);
        clearForm('engine-form');
        clearForm('meter-form');
        clearForm('valve-form');
        dispatch({ type: EDIT_IRRIGATION, payload: res.data });
    }).catch((err) => { error(err.response.statusText) });
  }
}

/**
* deleteIrrigation(): Deletes an existing irrigation record
* @param {String} irrigationId: unique id to the irrigation log
* @returns {Array} prevMeters: updated previous meter readings
* @called-in: ['/irrigation/irrigation_create.js']
**/
export function deleteIrrigation(irrigationId) {
  return (dispatch) => {
    axios.post(`${ROOT_URL}/deleteIrrigation`, { irrigationId })
      .then((res) => {
        let { irrigationId, prevMeters } = res.data;
        success('Success deleting an irrigation record');
        dispatch({ type: DELETE_IRRIGATION, payload: irrigationId, prevMeters });
    }).catch((err) => { error(err) });
  }
}
/**
* prepareData(): Gathers data into a submitable object; computes any remaining calculations
* @param {Object} formData: Date, employee, notes fields
* @param {Object} engineLog: Engine log: hours, engineId, irrigationId, totalFuelGal
* @param {Object} meterLog: engineId, irrigationId, meterId, endGal, startGal, totalGal
* @param {Object} valveLog: engineId, irrigationId, meterId, valveId, totalGal, hours
* @param {Object} user: user object
* @returns {Object} irrigation log object
* @called-in: ['/irrigation/irrigation_create.js']
**/
export function prepareData({ formData, engineLog, meterLog, valveLog, user }) {
  let irrigationId = generateID(15);
  return {
    type: PREPARE_IRRIGATION_DATA,
    payload: {
      data: {
        ...formData,
        irrigationId,
        submitter: user.id,
        totalEngineHours: sumProp('hours', engineLog),
        totalMeterGal: sumProp('totalGal', meterLog),
        totalValveHours: sumProp('hours', valveLog),
        totalValveGal: sumProp('totalGal', valveLog)
      },
      engineLog: engineLog.map((e) => { return { ...e, irrigationId }; }),
      meterLog: meterLog.map((e) => { return { ...e, irrigationId }; }),
      valveLog: valveLog.map((e) => { return { ...e, irrigationId }; })
    }
  }
}
/**
* prepareEditData(): Gathers edited form data into a submitable object; computes any remaining calculations
* @param {Object} formData: Date, employee, notes fields
* @param {Object} engineLog: Engine log: hours, engineId, irrigationId, totalFuelGal
* @param {Object} meterLog: engineId, irrigationId, meterId, endGal, startGal, totalGal
* @param {Object} valveLog: engineId, irrigationId, meterId, valveId, totalGal, hours
* @param {Object} user: user object
* @returns {Object} irrigation log object
* @called-in: ['/irrigation/irrigation_create.js']
**/
export function prepareEditData({ formData, irrigationId, engineLog, meterLog, valveLog, user }) {
  return {
    type: PREPARE_EDIT_IRRIGATION_DATA,
    payload: {
      data: {
        ...formData,
        irrigationId,
        submitter: user.id,
        totalEngineHours: sumProp('hours', engineLog),
        totalMeterGal: sumProp('totalGal', meterLog),
        totalValveHours: sumProp('hours', valveLog),
        totalValveGal: sumProp('totalGal', valveLog),
        editedBy: user.id,
        dateEdited: moment().format('YYYY-MM-DD')
      },
      engineLog: engineLog.map((e) => { return { ...e, irrigationId }; }).sort((a,b) => a.engineId < b.engineId ? -1 : 1),
      meterLog: meterLog.map((e) => { return { ...e, irrigationId }; }).sort((a,b) => a.meterId < b.meterId ? -1 : 1),
      valveLog: valveLog.map((e) => { return { ...e, irrigationId }; }).sort((a,b) => a.valveId < b.valveId ? -1 : 1)
    }
  }
}
/**
* handleValveHours(): onChange/onBlur function which adds/removes valve hours when inputted.
  If blank, filter; if value concat/modify
* @param {Object} valveRecord: engineId, irrigationId, meterId, endGal, startGal, totalGal
* @param {Boolean} edit: is a record being edited
* @returns {Object}
* @called-in: ['/irrigation/irrigation_create.js->irrigation_form.js->irrigation_valve_hours.js']
**/
export function handleValveHours({v, edit}) {
  if (edit) {
    return {
      type: HANDLE_EDIT_VALVE_HOURS,
      payload: v
    }
  }
  return {
    type: HANDLE_VALVE_HOURS,
    payload: v
  }
}

/**
* handleEngineHours(): onChange/onBlur function which adds/removes engine hours when inputted.
  If blank, filter; if value concat/modify
* @returns {Object}
* @called-in: ['/irrigation/irrigation_create.js->irrigation_form.js->irrigation_engine_hours.js']
**/
export function handleEngineHours({e, edit}) {
  if (edit) {
    return {
      type: HANDLE_EDIT_ENGINE_HOURS,
      payload: e
    }
  }
  return {
    type: HANDLE_ENGINE_HOURS,
    payload: e
  }
}

/**
* handleMeterGallons(): onChange/onBlur function which adds/removes meter gallons when inputted.
  If blank, filter; if value concat/modify
* @returns {Object}
* @called-in: ['/irrigation/irrigation_create.js->irrigation_form.js->irrigation_meter_gallons.js']
**/
export function handleMeterGallons({m, edit}) {
  if (edit) {
    return {
      type: HANDLE_EDIT_METER_GALLONS,
      payload: m
    }
  }
  return {
    type: HANDLE_METER_GALLONS,
    payload: m
  }
}

/**
* preloadMeterForm(): Preloads the meters form with the last submitted irrigation meter log
  If blank, filter; if value concat/modify
* @param {Array} prevMeters: Last submitted meter values
* @param {String} formId: id of the form to be filled
* @returns {Object}
* @called-in: ['/irrigation/irrigation_create.js']
**/
export function preloadMeterForm({prevMeters, formId}) {
  setTimeout(() => {
    preFillForm({ data: prevMeters, formId, prop: 'endGal' })
  }, 200);
  return {
    type: PRELOAD_METER_FORM
  }
}

/**
* selectIrrigationLogToEdit(): Selects a log to edit and prefills edit form
* @param {Object} i: irrigation log
* @returns {Object}
* @called-in: ['/irrigation/irrigation_manage.js']
**/
export function selectIrrigationLogToEdit({i, employees}) {
  let { engineLog, meterLog, valveLog } = i;
  // Need new copy of log; otherwise delete will mutate state
  let irr = { ...i };

  delete irr.engineLog;
  delete irr.meterLog;
  delete irr.valveLog;

  let emp = employees.find((e) => Number(e.id) === Number(irr.employee));
  let name = `${emp.firstName} ${emp.lastName}` || 'N/A';
  setTimeout(() => {
    clearForm('engine-form');
    clearForm('meter-form');
    clearForm('valve-form');
    document.getElementById('irrigation-date').value = moment(irr.date).format('YYYY-MM-DD');
    document.getElementById('irrigation-notes').value = irr.notes;
    document.getElementById('irrigation-employee').value = name;
    preFillForm({ data: engineLog.map((e) => { return { ...e, id: e.engineId } }), formId: 'engine-form', prop: 'hours' });
    preFillForm({ data: valveLog.map((e) => { return { ...e, id: e.valveId } }), formId: 'valve-form', prop: 'hours', style: true });
    preFillMeterForm({data: meterLog, formId: 'meter-form' });
  }, 500);

  return {
    type: SELECT_IRRIGATION_LOG_TO_EDIT,
    payload: {
      data: { ...irr },
      engineLog,
      meterLog,
      valveLog
    }
  }
}

/**
* resetDashboard(): Clears data for dashboard, returns to table view
* @returns {Object}
* @called-in: ['/irrigation/irrigation_view.js']
**/
export function resetDashboard() {
  return {
    type: RESET_DASHBOARD
  }
}

function error(err) {
  Materialize.toast(err, 4000, 'rounded red');
  return {
    type: ERROR,
    err
  };
}
function success(msg) {
  Materialize.toast(msg, 4000, 'rounded green');
}

export function _error(err) {
  Materialize.toast(err, 4000, 'rounded red');
  return {
    type: ERROR,
    err
  };
}



/* END */



// WEBPACK FOOTER //
// ./src/irrigation/irrigation_actions.js